import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Reset window scroll position on every route change.
 * Browsers (especially mobile Safari/Chrome) often preserve the previous scroll
 * position when navigating within a SPA — which means a visitor who scrolled
 * far down the property list lands halfway through the detail page and misses
 * the photo gallery at the top.
 *
 * This effect runs after each pathname change and scrolls to (0, 0). We use
 * `window.scrollTo` with `instant` behaviour because animated scrolling on
 * route change feels jarring and can race with route-level transitions.
 *
 * Hash links (e.g. `/page#section`) are respected — we skip the reset when
 * the URL contains a hash, so anchor navigation keeps working.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;
    // Skip /woningen: Properties.js manages its own scroll restoration via
    // sessionStorage so visitors returning from a detail page land back at
    // the card they were viewing. Force-scrolling to 0 here would fight
    // that logic.
    if (pathname === '/woningen') return;
    // Disable the browser's own scroll restoration so it can't fight us.
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    // Some mobile browsers ignore `instant` inside the same micro-task — retry.
    requestAnimationFrame(() => window.scrollTo(0, 0));
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
